import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import type { ColumnSort } from '@/lib/fundraising/sorting';
import { cn } from '@/lib/utils';

/** How long a list may take to answer before it shows it's loading — a quick answer never flickers. */
export const LOADING_DELAY_MS = 150;

/**
 * A column heading that sorts its list: a button (so the keyboard reaches it), `aria-sort` on the one the
 * list is sorted by, and a spinner while the list re-sorts on the server (`pending`). The spinner holds still
 * for anyone who asked for less motion.
 */
export function SortHeader<C extends string>({
  label,
  column,
  sort,
  onSort,
  pending = false,
  disabled = false,
  className,
  hint,
}: {
  label: string;
  column: C;
  /** How the list is sorted; null when it's in an order no column names (a call book's own). */
  sort: ColumnSort<C> | null;
  onSort: (column: C) => void;
  /** This column's re-sort is on its way; shown only after a moment. */
  pending?: boolean;
  disabled?: boolean;
  className?: string;
  /** What sorting this column means, when the heading alone doesn't say. */
  hint?: string;
}) {
  const active = sort?.column === column;
  const descending = active && sort.descending;
  const showPending = useDelayedFlag(pending, LOADING_DELAY_MS);
  const Arrow = active ? (descending ? ArrowDown : ArrowUp) : ArrowUpDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (descending ? 'descending' : 'ascending') : undefined}
      aria-busy={showPending || undefined}
      className={cn('px-3 font-semibold', className)}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        disabled={disabled}
        title={hint}
        className={cn(
          'group inline-flex h-8 items-center gap-1 rounded uppercase tracking-[inherit] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed',
          active && 'text-foreground',
        )}
      >
        {label}
        {showPending ? (
          <Loader2 aria-hidden="true" className="h-3 w-3 motion-safe:animate-spin" />
        ) : (
          <Arrow aria-hidden="true" className={cn('h-3 w-3', !active && 'opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60')} />
        )}
      </button>
    </th>
  );
}

/**
 * What a list shows while the rows on screen are the last read's and the next is on its way: a thin
 * indeterminate bar (none for anyone who asked for less motion) and a polite "Loading…" for screen readers.
 * The list dims its rows and sets `aria-busy` itself.
 */
export function StaleRowsIndicator({ shown }: { shown: boolean }) {
  return (
    <>
      {shown && (
        <div role="progressbar" aria-label="Loading" className="list-loading-line absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden motion-reduce:hidden">
          <span className="block h-full w-1/4 bg-primary" />
        </div>
      )}
      <span aria-live="polite" className="sr-only">
        {shown ? 'Loading…' : ''}
      </span>
    </>
  );
}
